import { Button, Stack } from "react-bootstrap"
import { useNavigate } from "react-router"
import { useReg } from "../api/reg/store"
import { useSearchParams } from "react-router";
import { useEffect, useState } from "react";
import Loading from "../components/Loading";
import { ERR_ACCESS_DENIED } from "../constants";
import Topbar from "../components/Topbar";
import { ReactComponent as CheckIcon } from "../svgs/check_badge.svg";
import { ReactComponent as BankIcon } from "../svgs/bank.svg";
import { ReactComponent as UsersIcon } from "../svgs/users.svg";
import Label from "../components/Label";

const InvalidInvite = () => {
    return <div className="center invite-container" style={{ paddingTop: "120px" }}>
        <Stack gap={3} className="text-center glassmorphism-card" style={{
            padding: "40px",
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
        }}>
            <Label size="24px" fontWeight="700" textAlign="center" color="#dc3545">Invite not found</Label>
            <Label size="16px" color="#636e72" textAlign="center" wrap={true}>Failed retrieving invite information. The link might be invalid or expired.</Label>
        </Stack>
    </div>
}

const InviteDeclined = () => {
    const navigate = useNavigate();
    return <div className="center invite-container" style={{ paddingTop: "120px" }}>
        <Stack gap={3} className="text-center glassmorphism-card" style={{
            padding: "40px",
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
        }}>
            <Label size="24px" fontWeight="700" textAlign="center">Invitation Declined</Label>
            <Label size="16px" color="#636e72" textAlign="center" wrap={true}>You have declined the invitation.</Label>
            <Button variant="outline-primary" onClick={() => navigate("/")} style={{ marginTop: "10px" }}>Back to Login</Button>
        </Stack>
    </div>
}

const InviteAcceptedScreen = () => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate("/");
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [navigate]);

    return (
        <>
            <Topbar />
            <div className="center invite-container" style={{ paddingTop: "120px" }}>
                <Stack gap={4} className="align-items-center glassmorphism-card" style={{
                    padding: "40px",
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
                }}>
                    <div style={{
                        background: "rgba(40, 167, 69, 0.1)",
                        borderRadius: "50%",
                        padding: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <CheckIcon fill="#28a745" width={60} height={60} />
                    </div>

                    <Stack gap={2} className="text-center">
                        <Label size="28px" fontWeight="700" textAlign="center" padding="0">Invite Accepted!</Label>
                        <Label size="16px" color="#636e72" textAlign="center" padding="0" wrap={true}>
                            You have successfully joined the platform.
                        </Label>
                    </Stack>

                    <div className="text-center">
                        <p style={{ color: "#0984e3", fontSize: "14px", fontWeight: "500", margin: 0 }}>
                            Redirecting you to login in <strong>{countdown}</strong> seconds...
                        </p>
                    </div>

                    <Button
                        variant="primary"
                        onClick={() => navigate("/")}
                        style={{
                            width: "100%",
                            padding: "12px",
                            borderRadius: "12px",
                            fontWeight: "600",
                            boxShadow: "0 4px 15px rgba(9, 132, 227, 0.2)"
                        }}
                    >
                        Login Now
                    </Button>
                </Stack>
            </div>
        </>
    );
}

const Invite = () => {
    const navigate = useNavigate()
    const { acceptInvite, loading, retrieveInviteDetails, inviteErr, inviteDetails } = useReg(state => state)
    const [failed, setFailed] = useState("")
    const searchParams = useSearchParams()
    const code = searchParams[0].get("code")
    const email = searchParams[0].get("email")
    const [accepted, setAccepted] = useState<boolean | null>(null)

    useEffect(() => {
        retrieveInviteDetails(code!, email!)
    }, [])

    if (inviteErr !== "") {
        switch (inviteErr) {
            case "200":
                break;
            // invite already accepted. needs to register
            case "invite_accepted":
                navigate("/reg")
                break;
            default:
                return <InvalidInvite />
        }
    }

    const onInviteAccept = async () => {
        const res = await acceptInvite(code!, email!)
        switch (res) {
            // success
            case "verified":
                setAccepted(true)
                break;
            case "invite_expired":
            case "invite_accepted":
                setFailed("invite expired")
                break;
            // need registration
            case "incomplete":
                navigate("/reg")
                break
            case ERR_ACCESS_DENIED:
                setFailed("Unable to resolve invitation details.")
                break;
            default:
                setFailed("Internal issue found.")
                break;
        }
    }

    if (accepted) {
        return <InviteAcceptedScreen />
    }

    if (accepted === false) {
        return <InviteDeclined />
    }

    if (loading) {
        return <Loading center />
    }

    return <>
        <Topbar />
        <div className="center invite-container" style={{ paddingTop: "80px" }}>
            <Stack gap={4} className="glassmorphism-card" style={{
                padding: "40px",
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
            }}>
                <div style={{ textAlign: "center", marginBottom: "10px" }}>
                    <div style={{
                        background: "rgba(103, 158, 255, 0.1)",
                        borderRadius: "20px",
                        padding: "15px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "15px",
                        overflow: "hidden"
                    }}>
                        {inviteDetails?.icon ? (
                            <img src={inviteDetails.icon} alt="App Icon" style={{ width: "60px", height: "60px", objectFit: "contain" }} />
                        ) : (
                            <BankIcon fill="#0984e3" width={50} height={50} />
                        )}
                    </div>
                    <Label size="26px" fontWeight="700" textAlign="center" padding="0">Platform Invitation</Label>
                    <Label size="15px" color="#636e72" textAlign="center" padding="5px 0 0" wrap={true}>Welcome to CoreOne Banking systems</Label>
                </div>

                <Stack gap={3} style={{ background: "rgba(255, 255, 255, 0.3)", padding: "20px", borderRadius: "15px" }}>
                    <Stack direction="horizontal" gap={3}>
                        <BankIcon fill="#565656" width={20} height={20} />
                        <div style={{ flex: 1 }}>
                            <Label size="12px" color="#636e72" fontWeight="600" padding="0">COMPANY</Label>
                            <Label size="16px" fontWeight="600" padding="0" wrap={true}>{inviteDetails?.com}</Label>
                        </div>
                    </Stack>

                    {inviteDetails?.app !== "global" && (
                        <Stack direction="horizontal" gap={3}>
                            <div style={{ width: "20px", display: "flex", justifyContent: "center" }}>
                                <CheckIcon fill="#565656" width={18} height={18} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Label size="12px" color="#636e72" fontWeight="600" padding="0">APPLICATION</Label>
                                <Label size="16px" fontWeight="600" padding="0" wrap={true}>{inviteDetails?.app}</Label>
                            </div>
                        </Stack>
                    )}

                    <Stack direction="horizontal" gap={3}>
                        <UsersIcon fill="#565656" width={20} height={20} />
                        <div style={{ flex: 1 }}>
                            <Label size="12px" color="#636e72" fontWeight="600" padding="0">ASSIGNED ROLES</Label>
                            <Label size="15px" fontWeight="500" padding="0" wrap={true}>
                                {inviteDetails?.roles.join(", ")}
                            </Label>
                        </div>
                    </Stack>
                </Stack>

                {failed === "" ? (
                    <Stack gap={3} style={{ marginTop: "10px" }}>
                        <Button
                            onClick={onInviteAccept}
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "12px",
                                fontWeight: "600",
                                background: "#0984e3",
                                border: "none"
                            }}
                        >
                            Accept Invitation
                        </Button>
                        <Button
                            variant="outline-danger"
                            onClick={() => setAccepted(false)}
                            style={{
                                width: "100%",
                                padding: "10px",
                                borderRadius: "12px",
                                fontWeight: "500",
                                border: "1px solid #ff7675",
                                color: "#ff7675"
                            }}
                        >
                            Decline
                        </Button>
                    </Stack>
                ) : (
                    <div style={{
                        background: "rgba(255, 118, 117, 0.1)",
                        padding: "15px",
                        borderRadius: "10px",
                        textAlign: "center"
                    }}>
                        <Label size="14px" color="#d63031" fontWeight="600" wrap={true}>{failed.toUpperCase()}</Label>
                    </div>
                )}
            </Stack>
        </div>
    </>
}


export default Invite